import React from "react";
import { Box, Button, CircularProgress, Dialog, Typography } from "@mui/material";
import { collectionColors, font, outlinedButtonSx, primaryButtonSx, subtitleSx } from "./tokens";

interface ModalShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  width?: number;
  confirmLabel: string;
  onConfirm: () => void;
  confirmDisabled?: boolean;
  isSubmitting?: boolean;
  /** Red confirm button for destructive actions. */
  danger?: boolean;
  /** Drop the Cancel button, for read-only dialogs whose only action is the confirm one. */
  hideCancel?: boolean;
  children?: React.ReactNode;
}

/**
 * Dialog chrome shared by every collection modal: close button, header, scrollable
 * body and the Cancel/confirm footer. Uses flow layout instead of the absolute
 * positioning of the older modals so the body can grow with its content.
 */
const ModalShell: React.FC<ModalShellProps> = ({
  open,
  onClose,
  title,
  subtitle,
  width = 512,
  confirmLabel,
  onConfirm,
  confirmDisabled = false,
  isSubmitting = false,
  danger = false,
  hideCancel = false,
  children,
}) => (
  <Dialog
    open={open}
    onClose={isSubmitting ? undefined : onClose}
    PaperProps={{
      sx: {
        boxSizing: "border-box",
        width: `${width}px`,
        maxWidth: "calc(100vw - 32px)",
        background: collectionColors.white,
        border: `1px solid ${collectionColors.border}`,
        boxShadow: "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1)",
        borderRadius: "10px",
      },
    }}
  >
    <Box sx={{ display: "flex", flexDirection: "column", padding: "25px", gap: "20px" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
          <Typography
            sx={{
              fontFamily: font,
              fontWeight: 600,
              fontSize: "18px",
              lineHeight: "18px",
              letterSpacing: "-0.439453px",
              color: collectionColors.textPrimary,
            }}
          >
            {title}
          </Typography>
          {subtitle && <Typography sx={subtitleSx}>{subtitle}</Typography>}
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "16px",
            height: "16px",
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
            opacity: 0.7,
            "&:hover": { opacity: 1 },
            "&:disabled": { opacity: 0.3, cursor: "default" },
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 4L12 12M12 4L4 12" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" />
          </svg>
        </Box>
      </Box>

      {/* Body */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          maxHeight: "calc(100vh - 260px)",
          overflowY: "auto",
          // Room for focus rings / dashed borders that would otherwise be clipped.
          margin: "-2px",
          padding: "2px",
        }}
      >
        {children}
      </Box>

      {/* Footer */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
        {!hideCancel && (
          <Button onClick={onClose} disabled={isSubmitting} sx={outlinedButtonSx}>
            Cancel
          </Button>
        )}
        <Button
          onClick={onConfirm}
          disabled={confirmDisabled || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={14} sx={{ color: collectionColors.white }} /> : undefined}
          sx={
            danger
              ? {
                  ...primaryButtonSx,
                  background: collectionColors.danger,
                  "&:hover": { background: "#b91c1c" },
                  "&.Mui-disabled": { background: collectionColors.danger, opacity: 0.5, color: collectionColors.white },
                }
              : primaryButtonSx
          }
        >
          {confirmLabel}
        </Button>
      </Box>
    </Box>
  </Dialog>
);

export default ModalShell;
